import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, type NavigateFunction } from 'react-router-dom';
import { buildApiUrl } from '../utils/apiConfig';

export type CoachDataLike = { id?: string; firstName?: string; lastName?: string } | null;

/** Where the admin/staff user entered the athlete view — drives breadcrumb root segment. */
export type AdminBreadcrumbOrigin = 'directory' | 'team';

/** Passed through React Router `location.state` for admin athlete views */
export type AthleteAdminNavState = {
    teamMemberId?: string;
    teamMemberName?: string;
    athleteEmail?: string;
    /** Display name for breadcrumb third segment (e.g. "Olivia Dean") */
    athleteDisplayName?: string;
    /** Set when opening an athlete from the directory or team member roster */
    breadcrumbOrigin?: AdminBreadcrumbOrigin;
};

/**
 * Directory: Directory / {athlete} [/ {page}]
 * Team roster: Team / {team member} / {athlete} [/ {page}]
 */
export function resolveAdminBreadcrumbOrigin(
    navState: AthleteAdminNavState | null | undefined
): AdminBreadcrumbOrigin {
    if (navState?.breadcrumbOrigin === 'team') return 'team';
    if (navState?.breadcrumbOrigin === 'directory') return 'directory';
    if (navState?.teamMemberId?.trim()) return 'team';
    return 'directory';
}

/**
 * Resolves the team member (staff) segment of the admin athlete breadcrumb.
 * Pass assigned staff name from GET /api/athletes/{id} (staffName) when nav state is missing.
 * Avoids using the logged-in user's banner name as a false "team member" label.
 */
export function getTeamMemberBreadcrumbLabel(
    coachData: CoachDataLike,
    navTeamMemberName: string | undefined,
    assignedStaffNameFromAthlete: string | undefined
): string {
    const fromCoach = coachData
        ? `${coachData.firstName || ''} ${coachData.lastName || ''}`.trim()
        : '';
    if (fromCoach) return fromCoach;
    const fromNav = navTeamMemberName?.trim();
    if (fromNav) return fromNav;
    const fromAthlete = assignedStaffNameFromAthlete?.trim();
    if (fromAthlete) return fromAthlete;
    return 'Staff';
}

/** Navigate to team member profile; falls back to staffId from athlete API, then /team. */
export function navigateToTeamMemberFromBreadcrumb(
    navigate: NavigateFunction,
    teamMemberIdFromState: string | undefined,
    staffIdFromAthlete: number | null | undefined
): void {
    const fromState = teamMemberIdFromState?.trim();
    if (fromState) {
        navigate(`/team/view/${encodeURIComponent(fromState)}`);
        return;
    }
    if (staffIdFromAthlete != null && staffIdFromAthlete > 0) {
        navigate(`/team/view/${encodeURIComponent(String(staffIdFromAthlete))}`);
        return;
    }
    navigate('/team');
}

/**
 * Fetches assigned staff id/name for admin athlete URLs when router state does not include teamMemberId.
 */
export function useAthleteStaffForAdminBreadcrumb(
    athleteId: string | undefined,
    enabled: boolean
): { staffId: number | null; staffName: string | null } {
    const [staffId, setStaffId] = useState<number | null>(null);
    const [staffName, setStaffName] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled || !athleteId?.trim()) {
            setStaffId(null);
            setStaffName(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    buildApiUrl(`api/athletes/${encodeURIComponent(athleteId.trim())}`),
                    {
                        credentials: 'include',
                        headers: { Accept: 'application/json' },
                    }
                );
                if (!res.ok || cancelled) return;
                const data = await res.json();
                if (cancelled) return;
                if (typeof data.staffId === 'number' && data.staffId > 0) {
                    setStaffId(data.staffId);
                } else {
                    setStaffId(null);
                }
                const n =
                    typeof data.staffName === 'string' && data.staffName.trim()
                        ? data.staffName.trim()
                        : null;
                setStaffName(n);
            } catch {
                if (!cancelled) {
                    setStaffId(null);
                    setStaffName(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [athleteId, enabled]);

    return { staffId, staffName };
}

/**
 * Athlete name for breadcrumb when not already in navigation state (e.g. direct URL / refresh).
 */
export function useAdminAthleteDisplayName(
    athleteId: string | undefined,
    navAthleteDisplayName: string | undefined
): string {
    const [fetched, setFetched] = useState<string | null>(null);

    useEffect(() => {
        if (!athleteId?.trim()) {
            setFetched(null);
            return;
        }
        if (navAthleteDisplayName?.trim()) {
            setFetched(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    buildApiUrl(`api/athletes/${encodeURIComponent(athleteId.trim())}`),
                    {
                        credentials: 'include',
                        headers: { Accept: 'application/json' },
                    }
                );
                if (!res.ok || cancelled) return;
                const data = await res.json();
                const name =
                    data?.displayName?.trim() ||
                    [data?.firstName, data?.lastName].filter(Boolean).join(' ').trim() ||
                    'Athlete';
                if (!cancelled) setFetched(name);
            } catch {
                if (!cancelled) setFetched('Athlete');
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [athleteId, navAthleteDisplayName]);

    const fromNav = navAthleteDisplayName?.trim();
    if (fromNav) return fromNav;
    if (fetched) return fetched;
    return 'Athlete';
}

function BreadcrumbSeparator() {
    return (
        <span className="breadcrumb-separator" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M6 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

type AthleteAdminBreadcrumbNavProps = {
    /**
     * Fourth segment (e.g. "Sessions"). Omit on the athlete profile page — breadcrumb ends at the athlete name.
     */
    pageLabel?: string;
    /** Second segment when drilled down from a team member roster */
    teamMemberLabel?: string;
    onTeamMemberClick?: () => void;
    /** Third segment — athlete being managed */
    athleteLabel: string;
    /** Athlete profile id for linking the third segment to their profile (ignored when `pageLabel` is omitted — already on profile) */
    athleteProfileId?: string;
    className?: string;
    'data-testid'?: string;
};

/**
 * Breadcrumb (directory): Directory / {athlete} [/ {page}]
 * Breadcrumb (team): Team / {team member} / {athlete} [/ {page}]
 */
export function AthleteAdminBreadcrumbNav({
    pageLabel,
    teamMemberLabel,
    onTeamMemberClick,
    athleteLabel,
    athleteProfileId,
    className = 'breadcrumbs-above-layout',
    'data-testid': dataTestId,
}: AthleteAdminBreadcrumbNavProps) {
    const navClassName = ['athlete-admin-breadcrumb', className].filter(Boolean).join(' ');
    const navigate = useNavigate();
    const location = useLocation();
    const navState = location.state as AthleteAdminNavState | null;
    const fromTeamRoster = resolveAdminBreadcrumbOrigin(navState) === 'team';

    const showPageSegment = Boolean(pageLabel?.trim());
    const preserveNavState = location.state as AthleteAdminNavState | undefined;

    const handleAthleteClick = () => {
        if (!athleteProfileId?.trim()) return;
        navigate(`/athlete/profile/${encodeURIComponent(athleteProfileId.trim())}`, {
            state: preserveNavState,
        });
    };

    const athleteSegment = showPageSegment ? (
        athleteProfileId?.trim() ? (
            <button type="button" onClick={handleAthleteClick} className="breadcrumb-link">
                {athleteLabel}
            </button>
        ) : (
            <span className="breadcrumb-athlete-name">{athleteLabel}</span>
        )
    ) : (
        <span className="breadcrumb-current">{athleteLabel}</span>
    );

    if (!fromTeamRoster) {
        return (
            <nav className={navClassName} aria-label="Breadcrumb" data-testid={dataTestId}>
                <button type="button" onClick={() => navigate('/athletes-directory')} className="breadcrumb-link">
                    Directory
                </button>
                <BreadcrumbSeparator />
                {athleteSegment}
                {showPageSegment && (
                    <>
                        <BreadcrumbSeparator />
                        <span className="breadcrumb-current">{pageLabel}</span>
                    </>
                )}
            </nav>
        );
    }

    return (
        <nav className={navClassName} aria-label="Breadcrumb" data-testid={dataTestId}>
            <button type="button" onClick={() => navigate('/team')} className="breadcrumb-link">
                Team
            </button>
            <BreadcrumbSeparator />
            <button type="button" onClick={onTeamMemberClick} className="breadcrumb-link">
                {teamMemberLabel || 'Staff'}
            </button>
            <BreadcrumbSeparator />
            {athleteSegment}
            {showPageSegment && (
                <>
                    <BreadcrumbSeparator />
                    <span className="breadcrumb-current">{pageLabel}</span>
                </>
            )}
        </nav>
    );
}
